import React, { useEffect, useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { dayjsInit } from '@ow/utils'
import { css, Global } from '@emotion/react'
import { useClickOutside } from '@mantine/hooks'
import { StyledWrapper, StyledRange, StyleRangeWrapper } from './styled'
import Input from '../input/input-base'
import RenderIf from '../render-if'
import PickerWithTime from './components/picker-with-time'
import MonthPicker from './components/month-picker'

// 日期格式化
const formatDate = (time, formatter = 'YYYY-MM-DD') => {
  return dayjsInit(time).format(formatter)
}

const DatePickerRange = (props) => {
  const {
    style,
    inputStyle,
    ranges,
    placeholder,
    onChange,
    currentDate,
    id = 'dateRange',
    showTime = false,
    showMonth,
    monthType,
    allowClear,
    ...otherProps
  } = props
  const [show, setShow] = useState(false)
  const [date, setDate] = useState(ranges)
  const { startDate, endDate } = date[0]
  const inputRef = useRef()
  const [rangeStyle, setRangeStyle] = useState()
  const datePickerRef = useClickOutside(() => setShow(false))
  useEffect(() => {
    setDate(ranges)
  }, [ranges])
  const FORMATTER = showTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD'
  const dateStr =
    startDate && endDate
      ? `${formatDate(new Date(startDate), FORMATTER)} - ${formatDate(
          new Date(endDate),
          FORMATTER
        )}`
      : ''
  const isMultiMonth = monthType === 'range'

  const onChangeDate = (item) => {
    const dateInfo = item.selection || item.range1 || {}
    const start = dateInfo.startDate || ''
    const end = dateInfo.endDate || ''
    setDate([{ startDate: start, endDate: end }])
    if (start && end) {
      if (start !== end) {
        setShow(false)
      }
      if (onChange) {
        onChange([{ startDate: start, endDate: end }])
      }
    }
  }
  const onMonthSelect = useCallback(
    (v) => {
      const month = isMultiMonth
        ? [v]
        : [
            {
              startDate: v.startOf('month').toDate(),
              endDate: v.endOf('month').toDate(),
            },
          ]

      setDate(month)

      if (onChange) {
        onChange(month)
      }
    },
    [onChange]
  )

  const initDateRangeStyle = () => {
    if (!showTime || !inputRef.current) return
    const inputStyles = inputRef.current.getBoundingClientRect()
    const { left = 0 } = inputStyles
    if (left + 345 < window.innerWidth) return
    setRangeStyle({
      left: 'auto',
      right: '0',
    })
  }
  useEffect(() => {
    if (typeof document !== 'object') {
      return
    }
    // 控制位置，防止日期选择溢出屏幕
    initDateRangeStyle()
  }, [])
  useEffect(() => {
    if (!show) return
    initDateRangeStyle()
  }, [show])
  const resetDateRange = () => {
    setDate([{ startDate: null, endDate: null }])
    if (onChange) {
      onChange([{ startDate: null, endDate: null }])
    }
  }

  return (
    <StyledWrapper>
      <Global
        styles={css`
          .dark {
            .form-input {
              outline: 0;
              box-shadow: 0 0 0 3px transparent;
              border: 1px solid var(--ow-color-darkTheme-800);
            }
            .rdrDateRangeWrapper {
              background: var(--ow-color-darkTheme-1100);
              border: 1px solid var(--ow-color-darkTheme-800);
            }
            .rdrPprevButton {
              i {
                border-color: transparent #fff transparent transparent !important;
              }
            }
            .rdrNextButton {
              i {
                border-color: transparent transparent transparent #fff !important;
              }
            }
            .rdrNextPrevButton,
            .rdrInRange {
              background: var(--ow-color-darkTheme-800) !important;
            }
            .rdrDays button {
              background: var(--ow-color-darkTheme-1100) !important;
              span:hover {
                background: var(--ow-color-darkTheme-600) !important;
                cursor: pointer;
                line-height: 22px;
              }
            }
            .rdrDayNumber,
            .rdrMonthAndYearPickers select {
              color: #fff !important;
            }
            .rdrEndEdge {
              background: var(--ow-color-primary-600) !important;
            }
          }
        `}
      />
      {showMonth ? (
        <MonthPicker
          className={props.className}
          style={props.style}
          placeholder={placeholder}
          isMulti={isMultiMonth}
          value={isMultiMonth ? date[0] : startDate}
          onChange={onMonthSelect}
        />
      ) : (
        <div id={id} className="Calendar">
          <Input
            className="dark:bg-darkTheme-1100"
            style={{ width: '200px', ...inputStyle }}
            placeholder={placeholder}
            value={dateStr}
            onFocus={() => setShow(true)}
            onChange={resetDateRange}
            ref={inputRef}
            allowClear={allowClear}
          />
          <RenderIf isTrue={show}>
            <StyleRangeWrapper style={rangeStyle} ref={datePickerRef}>
              {showTime ? (
                <PickerWithTime
                  style={style}
                  onChnage={onChangeDate}
                  {...otherProps}
                />
              ) : (
                <StyledRange
                  style={style}
                  ranges={date}
                  onChange={onChangeDate}
                  shownDate={currentDate}
                  {...otherProps}
                />
              )}
            </StyleRangeWrapper>
          </RenderIf>
        </div>
      )}
    </StyledWrapper>
  )
}

// 参数类型
DatePickerRange.propTypes = {
  style: PropTypes.shape({}),
  className: PropTypes.string,
  locale: PropTypes.shape({}),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  showMonthAndYearPickers: PropTypes.bool,
  showDateDisplay: PropTypes.bool,
  currentDate: PropTypes.shape(Date),
  minDate: PropTypes.shape(Date),
  maxDate: PropTypes.shape(Date),
  disabledDates: PropTypes.bool,
  disabledDay: PropTypes.bool,
  showMonthArrow: PropTypes.bool,
  navigatorRenderer: PropTypes.func,
  editableDateInputs: PropTypes.bool,
  initialFocusedRange: PropTypes.func,
  focusedRange: PropTypes.func,
  preview: PropTypes.func,
  showPreview: PropTypes.bool,
  onPreviewChange: PropTypes.func,
  dateDisplayFormat: PropTypes.string,
  dayDisplayFormat: PropTypes.string,
  monthDisplayFormat: PropTypes.string,
  dayContentRenderer: PropTypes.func,
  dragSelectionEnabled: PropTypes.bool,
  selectedDateColor: PropTypes.string,
  startDateBackground: PropTypes.string,
  endDateBackground: PropTypes.string,
}
DatePickerRange.defaultProps = {
  style: undefined,
  className: undefined,
  locale: undefined,
  id: undefined,
  placeholder: undefined,
  onChange: undefined,
  currentDate: undefined,
  minDate: undefined,
  maxDate: undefined,
  disabledDates: undefined,
  disabledDay: undefined,
  showMonthArrow: undefined,
  showMonthAndYearPickers: undefined,
  navigatorRenderer: undefined,
  showDateDisplay: undefined,
  initialFocusedRange: undefined,
  focusedRange: undefined,
  preview: undefined,
  showPreview: undefined,
  onPreviewChange: undefined,
  dateDisplayFormat: undefined,
  dayDisplayFormat: undefined,
  monthDisplayFormat: undefined,
  dayContentRenderer: undefined,
  editableDateInputs: undefined,
  dragSelectionEnabled: undefined,
  selectedDateColor: undefined,
  startDateBackground: undefined,
  endDateBackground: undefined,
}
export default DatePickerRange
